import Api from "../../../../lib/api";
import ServiceLocator from "../../../../global/service-locator";
import {declension} from "../../../../lib";
import checkCourseLang from './course-lang';

export default function init() {
	return new DataManager();
}

class DataManager {
	constructor() {
		this.cityId = null;
		this.entryId = null;
		this.courseLength = null;
		this.courseFrequency = null;
		this.startDate = null;
		this.placesLastWeek = null;
		this.placesLeft = null;
		this.age = null;
		this.courseLang = null;
		this.priceSite = {
			initial: null,
			parts: null,
			final: null,
			upfront: null,
			paymentType: null
		};
		this.priceLMS = null;

		this.sl = ServiceLocator.instance;

		this.init();
	}

	get lengthText() {
		if (!this.courseLength) {
			return null;
		}

		return `${this.courseLength} ${declension(this.courseLength, craft.localization.occupation)}`
	}

	get frequencyText() {
		if (!this.courseFrequency) {
			return null;
		}

		const value = typeof this.courseFrequency === 'string' ?
			this.courseFrequency.match(/\d+/)[0] :
			this.courseFrequency;

		return `${value} ${declension(value, craft.localization.occupation)} ${craft.localization.perWeek}`;
	}

	get startDateText() {
		if (!this.startDate) {
			if (craft.page.coursesStatsConfig.startDate) {
				return `${craft.localization.start} ${craft.page.coursesStatsConfig.startDate}`;
			}

			return null;
		}

		return craft.page.abTest ?
			`${craft.localization['page.course.hero.descriptor.studyingStart']} <b class="bold">${this.startDate.getDate()} ${this.getWhenStartMonth(this.startDate.getMonth())}</b>`
			: `${craft.localization.start} ${this.startDate.getDate()} ${this.getWhenStartMonth(this.startDate.getMonth())}`;
	}

	get lastWeekTextObj() {
		if (!this.placesLastWeek || this.placesLastWeek < 3) {
			return null;
		}

		return {
			label: this.placesLastWeek === 1 ?
				craft.localization.peoplePerWeek[0] :
				craft.localization.peoplePerWeek[1],
			value: `${this.placesLastWeek} ${declension(this.placesLastWeek, craft.localization.people)}`
		}
	}

	get placesLeftText() {
		if (!this.placesLeft || this.placesLeft > 3) {
			return null;
		}

		return `${this.placesLeft} ${declension(this.placesLeft, craft.localization.places)}`
	}

	get initialPriceText() {
		if ((!this.priceLMS || !this.priceLMS.initial_cost) && !this.priceSite.initial) {
			return null;
		}

		let value = this.priceSite.initial;

		if (this.priceLMS && this.priceLMS.initial_cost) {
			value = this.priceLMS.initial_cost;
		}

		return `${this.separateThousands(value)} ₴`;
	}

	get pricePartsText() {
		if ((!this.priceLMS || !this.priceLMS.payparts) && !this.priceSite.parts) {
			return null;
		}

		let value = this.priceSite.parts;

		if (this.priceLMS && this.priceLMS.payparts) {
			value = this.priceLMS.payparts;
		}

		if (value === 666) {
			value = 667;
		}

		return `${this.separateThousands(value)} ₴`;
	}

	get upfrontDiscountText() {
		if ((!this.priceSite.upfront || this.priceSite.paymentType === 'full') && (!this.priceLMS || !this.priceLMS.full_price_payment_discount)) {
			return null;
		}

		return this.priceLMS && this.priceLMS.full_price_payment_discount ? `-${this.separateThousands(this.priceLMS.full_price_payment_discount)} ₴` : `-${this.separateThousands(this.priceSite.initial - this.priceSite.upfront)} ₴`;
	}

	get finalPriceText() {
		if ((!this.priceLMS || this.priceLMS.final_cost == null) && !this.priceSite.upfront) {
			return null;
		}

		let value = this.priceSite.paymentType === 'full' ? this.priceSite.initial : this.priceSite.upfront;

		if (this.priceLMS && this.priceLMS.final_cost != null) {
			value = this.priceLMS.final_cost;
		}

		return `${this.separateThousands(value)} ₴`;
	}

	get promocodeText() {
		if (!this.priceLMS || !this.priceLMS.promocode) {
			return null;
		}

		return `-${this.separateThousands(this.priceLMS.promocode)} ₴`;
	}

	get appliedDiscountsText() {
		if (!this.priceLMS || !this.priceLMS.applied_discounts) {
			return null;
		}

		let value = this.priceLMS.applied_discounts;

		if (this.priceLMS.promocode) {
			value -= this.priceLMS.promocode;
		}

		if (this.priceLMS.full_price_payment_discount) {
			value -= this.priceLMS.full_price_payment_discount;
		}

		if (value <= 0) {
			return 0;
		}

		return `-${this.separateThousands(value)} ₴`;
	}

	get writeOffText() {
		if (!this.priceLMS || !this.priceLMS.balance_write_off) {
			return null;
		}

		return `-${this.separateThousands(this.priceLMS.balance_write_off)} ₴`;
	}

	get frozenMoneyWriteOffText() {
		if (!this.priceLMS || !this.priceLMS.frozen_money_write_off) {
			return null;
		}

		return `-${this.separateThousands(this.priceLMS.frozen_money_write_off)} ₴`;
	}

	get mainBalanceWriteOffText() {
		if (!this.priceLMS || !this.priceLMS.balances ||
			!this.priceLMS.balances.money || !this.priceLMS.balances.money.selected) {
			return null;
		}

		return `-${this.priceLMS.balances.money.amount} ₴`;
	}

	get referralBalanceWriteOffText() {
		if (!this.priceLMS || !this.priceLMS.balances ||
			!this.priceLMS.balances.referral || !this.priceLMS.balances.referral.selected) {
			return null;
		}

		return `-${this.priceLMS.balances.referral.amount} ₴`;
	}

	get bonusBalanceWriteOffText() {
		if (!this.priceLMS || !this.priceLMS.balances ||
			!this.priceLMS.balances.bonus || !this.priceLMS.balances.bonus.selected) {
			return null;
		}

		return `-${this.priceLMS.balances.bonus.amount} ₴`;
	}

	init() {
		this.parseDefaultData();
		this.url = `${Api.restSiteUrl}/1.0/courses/statistics`;
		this.courseLang = costorage.get(`h_cl-${this.entryId}`);
	}

	parseDefaultData() {
		this.defaultData = craft.page.coursesStatsConfig;

		const {
			cityId,
			entryId,
			courseLength,
			courseFrequency,
			age,
			startDate
		} = craft.page.coursesStatsConfig;
		this.cityId = cityId;
		this.entryId = entryId;
		this.courseLength = courseLength;
		this.courseFrequency = courseFrequency;
		this.age = age;
	}

	fetch(cb) {
		const params = {};

		if (this.entryId) {
			params.entryId = this.entryId;
		}

		if (this.courseLang) {
			params.courseLang = this.courseLang;
		}

		const courseLangFromHash = checkCourseLang();

		if (courseLangFromHash && !this.courseLang) {
			params.courseLang = courseLangFromHash;
		}

		const promises = [
			Api.get(this.url, params)
				.then(data => {
					if (data && data.success) {
						this.parseFetchedData(data.response);
					} else {
						console.log('Api data fetch failed. Base url responded with error.');
					}
				}).catch(err => {
				console.error(err);
			})
		];

		if (craft.page.buyInLMS) {
			promises.push(Api.get(`${Api.lmsApiUrl}/course-store/site/courses/${craft.page.coursesStatsConfig.scheduleId}/price`, {}, {
				withCredentials: true
			}).then((data) => {
				if (data && data.success && data.response) {
					this.parseDataFromLms(data.response);
				}
			}).catch((err) => {
				console.error(err);
			}));
		}

		Promise.allSettled(promises).then((values) => {
			cb();
		});
	}

	parseFetchedData(data) {
		const {
			frequency,
			last_week,
			length,
			places,
			start_date,
			price,
			price_parts_int,
			price_upfront_int,
			payment_type
		} = data;

		if (length) {
			this.courseLength = length;
		}

		if (frequency) {
			this.courseFrequency = frequency
		}

		if (start_date) {
			this.startDate = new Date(start_date);
		}

		this.placesLastWeek = last_week;
		this.placesLeft = places;

		/* Course language for consultation form */
		if (data.courseLang) {
			this.sl.courseLang = data.courseLang;
		}

		this.priceSite.initial = price;
		this.priceSite.parts = price_parts_int;
		this.priceSite.upfront = price_upfront_int;
		this.priceSite.paymentType = payment_type;
	}

	parseDataFromLms(data) {
		this.priceLMS = {
			initial_cost: data.initial_cost,
			final_cost: data.final_cost,
			payparts: data.payparts
		};

		const promocode = data.all_discounts.find((discount) => discount.type === 'coupon');
		const full_price_payment_discount = data.all_discounts.find((discount) => discount.type === 'full');

		if (data.applied_discounts && data.applied_discounts !== 0) {
			this.priceLMS.applied_discounts = data.applied_discounts;
		}

		if (data.balance_write_off && data.balance_write_off !== 0) {
			this.priceLMS.balance_write_off = data.balance_write_off;
		}

		if (data.freeze_money && data.freeze_money !== 0) {
			this.priceLMS.frozen_money_write_off = data.freeze_money;
		}

		if (promocode) {
			this.priceLMS.promocode = +promocode.value;
		}

		if (full_price_payment_discount) {
			this.priceLMS.full_price_payment_discount = Math.round(data.initial_cost * 0.1);
		}

		if (data.balances) {
			this.priceLMS.balances = data.balances;
		}

		if (data.limitless_bonuses) {
			this.priceLMS.limitlessBonuses = data.limitless_bonuses;
		}
	}

	separateThousands(num) {
		return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	}

	convertToNum(month) {
		const monthNum = {
			'январь': 1,
			'січень': 1,
			'февраль': 2,
			'лютий': 2,
			'март': 3,
			'березень': 3,
			'апрель': 4,
			'квітень': 4,
			'май': 5,
			'травень': 5,
			'июнь': 6,
			'червень': 6,
			'июль': 7,
			'липень': 7,
			'август': 8,
			'серпень': 8,
			'сентябрь': 9,
			'вересень': 9,
			'октябрь': 10,
			'жовтень': 10,
			'ноябрь': 11,
			'листопад': 11,
			'декабрь': 12,
			'грудень': 12
		};

		return monthNum[month];
	}

	getWhenStartMonth(num) {
		const months = [
			craft.localization.monthsWhen.jan,
			craft.localization.monthsWhen.feb,
			craft.localization.monthsWhen.mar,
			craft.localization.monthsWhen.apr,
			craft.localization.monthsWhen.may,
			craft.localization.monthsWhen.jun,
			craft.localization.monthsWhen.jul,
			craft.localization.monthsWhen.aug,
			craft.localization.monthsWhen.sep,
			craft.localization.monthsWhen.oct,
			craft.localization.monthsWhen.nov,
			craft.localization.monthsWhen.dec
		];

		return months[num];
	}
}